/*
 * The API client. One `fetch` wrapper, one error type, and the queries the
 * screens read.
 *
 * WHY EVERY REQUEST GOES THROUGH `call()`. Three things have to be true of every
 * request and are easy to forget on any one of them: credentials must be included
 * (the session is a cookie), the base URL has to come from configuration rather
 * than be relative (under Capacitor the bundle is served from
 * `capacitor://localhost`, where a relative path resolves to the app itself), and
 * a failure has to arrive as a typed error rather than as an object with a
 * different shape. A helper is what makes those three unforgettable.
 *
 * THE RESPONSE SHAPES ARE `@dove/shared` TYPES, unchanged. That is the whole point
 * of the wire contract: the screens were written against `PostData`,
 * `PublicProfile`, `ViewerRelation` and the rest when those came from a fixture,
 * and they render the same objects now that a server sends them.
 */
import type {
  BirdPoseId,
  CrumbGiftOffer,
  CrumbGiftTarget,
  CrumbPurse,
  DoveBackgroundId,
  GifPage,
  GifPick,
  InviteWelcome,
  LetterData,
  LoftEntry,
  MentionCandidate,
  NotificationItem,
  PlumageId,
  PostData,
  PublicAuthor,
  PublicProfile,
  PublicSpace,
  SpaceListing,
  SpeciesId,
  ViewerRelation,
} from '@dove/shared';

/**
 * Where the API is, and it differs by channel rather than by environment.
 *
 * **On the web it is a RELATIVE path**, because the session is an httpOnly cookie
 * with `SameSite=Lax` and the app and the Worker are on different registrable
 * domains. A direct call would be cross-site, the browser would not send the
 * cookie, and it would present as "signed in, but every request is 401". So the
 * app calls `/api/*` on its own origin and something in front rewrites it to the
 * Worker: `public/_redirects` in production, the `server.proxy` block in
 * `vite.config.ts` in development. Both, deliberately — an arrangement where
 * development is cross-origin and production is not is how a cookie bug reaches
 * production having worked locally all week.
 *
 * **On a native shell it is the absolute Worker URL**, because there is no origin
 * to be relative to: the bundle is served from `capacitor://localhost`, where
 * `/api/...` resolves to the app itself. Native does not need the cookie either —
 * it sends a bearer token, because Apple and Google will not let an app read a
 * cookie set on a domain it deep-linked back from.
 *
 * `VITE_API_URL` overrides both, for pointing a build at a preview Worker.
 */
const NATIVE_API_URL = 'https://dove-api.majed-tarabein.workers.dev/api';

function isNativePlatform(): boolean {
  const global = globalThis as { Capacitor?: { isNativePlatform?: () => boolean } };
  return global.Capacitor?.isNativePlatform?.() === true;
}

const BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (isNativePlatform() ? NATIVE_API_URL : '/api');

/**
 * One fresh GET namespace per page load.
 *
 * A permanent redirect cached during the Netlify-to-Hobbyhood move is keyed by
 * the exact URL and can be reused before a network response exists. The no-store
 * policy remains, while this nonce makes those legacy keys unreachable. Native
 * never used the retired web origin.
 */
const WEB_GET_NONCE =
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function requestUrl(path: string, method: string | undefined): string {
  if (isNativePlatform() || (method ?? 'GET').toUpperCase() !== 'GET') {
    return `${BASE}${path}`;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${BASE}${path}${separator}__dove=${encodeURIComponent(WEB_GET_NONCE)}`;
}

/** The error every failed call throws. `code` is what a caller branches on. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The session token, for the native channel only.
 *
 * A CAPACITOR WEBVIEW CANNOT RELY ON THE COOKIE. The OAuth flow leaves the app for
 * the system browser, and neither Apple nor Google will let the app read a cookie
 * set on the domain it deep-linked back from — so on native the token is stored and
 * sent as a bearer header instead. On the web this stays null and the cookie does
 * the work, which is why it is not simply used everywhere: a token in JavaScript's
 * reach is readable by any script on the page, and an httpOnly cookie is not.
 */
let bearerToken: string | null = null;

export function setBearerToken(token: string | null): void {
  bearerToken = token;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  /*
   * PRESERVE THE WHOLE `HeadersInit` CONTRACT. Spreading a `Headers` instance
   * produces an empty object, and spreading tuple headers produces numeric keys;
   * both silently discard the caller's headers. `Headers` understands all three
   * legal forms and gives case-insensitive lookup, so a caller's explicit media
   * type always wins.
   *
   * ONLY STRINGS ARE JSON. A `URLSearchParams` body is form-encoded, and callers
   * that need that media type declare it with the body. Labelling every truthy
   * body as JSON would overwrite that distinction — and would do the same to
   * FormData.
   */
  const headers = new Headers(init.headers);
  if (typeof init.body === 'string' && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (bearerToken) headers.set('authorization', `Bearer ${bearerToken}`);

  let res: Response;
  try {
    res = await fetch(requestUrl(path, init.method), {
      ...init,
      headers,
      /*
       * NEVER CONSULT THE BROWSER HTTP CACHE FOR AN API REQUEST.
       *
       * API responses already say `no-store`; this is the client half, and it also
       * covers a cached redirect before a response exists. During the move from the
       * Netlify hostname, legacy clients cached a permanent /api/* redirect to the
       * new origin. The server-side rewrite was corrected later, but an HTTP cache
       * is allowed to keep answering with that old 301 without asking the server.
       * For an authenticated detail read that drops the origin-scoped cookie and
       * turns a real letter into the privacy-preserving 404.
       */
      cache: 'no-store',
      /* The session is an httpOnly cookie, and without this the browser sends it on
         navigations but not on `fetch` — which presents as "signed in, but every
         request is 401". */
      credentials: 'include',
    });
  } catch {
    /* A rejected fetch has no response body to explain itself. Turn it into the
       same typed, person-readable failure as an HTTP error rather than exposing a
       browser-specific "Failed to fetch" message (or making every form guess). */
    throw new ApiError(
      0,
      'network',
      'We couldn’t reach Dove. Check your connection and try again.',
    );
  }

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: { code?: string; message?: string } }).error
        : null;
    /*
     * BETTER AUTH DOES NOT USE OUR ERROR ENVELOPE, and the sign-in screen is where
     * that shows. `/auth/*` is Better Auth's own handler (see the Worker), and it
     * answers with a bare `{ "message": "..." }` — so a rate-limited sign-up came back
     * as the generic "Something went wrong." while the response actually said "Too
     * many requests. Please try again later.", which is the one sentence the person
     * needed. Read the flat `message` when the envelope is absent; `lib/errors` owns
     * everything else and always sends the envelope.
     */
    const flat =
      !err && body && typeof body === 'object' && 'message' in body
        ? (body as { message?: unknown }).message
        : null;
    const flatCode =
      !err && body && typeof body === 'object' && 'code' in body
        ? (body as { code?: unknown }).code
        : null;

    throw new ApiError(
      res.status,
      err?.code ?? (typeof flatCode === 'string' ? flatCode : 'internal'),
      err?.message ??
        (typeof flat === 'string' && flat ? flat : 'Something went wrong.'),
    );
  }
  return body as T;
}

/* --------------------------------------------------------------------------
 * Who I am
 * ----------------------------------------------------------------------- */

export interface MeResponse {
  me: PublicProfile | null;
  timezone?: string;
  needsOnboarding?: boolean;
  suggestedHandle?: string;
  suggestedName?: string;
  /**
   * The city the edge read off this connection, formatted for the card
   * (docs/5 § D260809-1521). Absent when it read none, and absent on a finished
   * account — a suggestion is for an empty field.
   */
  suggestedCity?: string;
  /**
   * Where this app lives on the web, for links that leave it (docs/5 § D260805-1222).
   *
   * FROM THE SERVER BECAUSE THE CLIENT CANNOT KNOW: under Capacitor
   * `window.location.origin` is `capacitor://localhost`. `lib/share` treats the
   * browser's own origin as a fallback, so an older API still shares working links
   * on the web — which is why this is optional rather than required.
   */
  webOrigin?: string;
}

export const getMe = (): Promise<MeResponse> => call('/me');

export interface OnboardingInput {
  handle: string;
  displayName: string;
  dob: string;
  city?: string;
  bio?: string;
  timezone?: string;
  /** The invitation this person arrived on, out of `lib/invite`. The server
   *  ignores one it cannot resolve rather than refusing the account. */
  inviteCode?: string;
}

export const finishOnboarding = (input: OnboardingInput) =>
  call<{ me: PublicProfile }>('/me/onboarding', {
    method: 'POST',
    body: JSON.stringify(input),
  });

/* --------------------------------------------------------------------------
 * Invitations
 * ----------------------------------------------------------------------- */

/**
 * Your own invite link (docs/5 § D260805-0328).
 *
 * A POST BECAUSE IT MINTS, not because of REST etiquette: the code is created on
 * the first ask, and while that was a GET, a `SameSite=Lax` session cookie meant
 * any page anywhere could navigate a signed-in person at it and mint one for them
 * without their knowledge. Idempotent all the same — the second call returns the
 * same code — which is why the sheet reads it with a query rather than a mutation.
 *
 * The URL is the SERVER's, not composed here, and that is the point of it being
 * on the wire at all: under Capacitor `window.location.origin` is
 * `capacitor://localhost`, so a link this client built would be one nobody could
 * open. The Worker knows the one canonical web origin.
 */
export const getMyInvite = () =>
  call<{ code: string; url: string }>('/me/invite', { method: 'POST' });

/**
 * Who an invitation is from — THE ONE CALL IN THIS FILE THAT WORKS WITH NO
 * SESSION, because the person reading its answer has no account yet.
 *
 * It still goes through `call()`, so it still sends credentials: somebody who is
 * already signed in and taps a friend's link hits the same endpoint, and the
 * server does not care either way.
 */
export const getInvitation = (code: string) =>
  call<{ inviter: InviteWelcome }>(`/invites/${encodeURIComponent(code)}`);

/**
 * The card's editable fields. Every one of them is optional: the edit form sends
 * ONLY what changed, so an untouched field is an absent key rather than a
 * resubmission of its current value.
 *
 * `''` MEANS CLEAR for the three that can be empty. That is why they are not
 * simply omitted when blank — omitting is "leave it alone", and there has to be a
 * way to un-write a bio. `birdName` clears back to the bird's default rather than
 * to nothing (`defaultBirdName`).
 */
export interface ProfilePatch {
  handle?: string;
  displayName?: string;
  bio?: string;
  city?: string;
  pigeonName?: string;
  timezone?: string;
  /** The bird's appearance (docs/5 § D260805-0318). Sent by the closet, not by the card —
   *  a colour is chosen by looking at the bird, so it has no field on a form. */
  plumage?: PlumageId;
  /** WHICH bird it is (docs/5 § D260805-1237). Same argument as `plumage` above and the
   *  same sender — you pick a bird by watching it, not by filling in a field. */
  species?: SpeciesId;
  /** WHICH MOMENT of it the portrait beside your name holds (docs/5 § D260806-1118).
   *  The closet's third drawer; the same argument as the two above. */
  pose?: BirdPoseId;
}

export const updateProfile = (patch: ProfilePatch) =>
  call<{ me: PublicProfile }>('/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * May I have this handle?
 *
 * ONE INDEXED LOOKUP PER DISTINCT STRING, and the client's job is to keep the
 * number of distinct strings small: the caller debounces, skips anything the
 * pattern already rejects, skips the name the person is already wearing, and lets
 * react-query cache the answer forever (`staleTime: Infinity`) so backspacing
 * through a name asks nothing twice. See `useHandleAvailability`.
 *
 * THE MESSAGE IS THE SERVER'S OWN SENTENCE and is rendered verbatim. There is one
 * wording of "taken" in this product and it is not written here.
 */
/* --------------------------------------------------------------------------
 * Bird portraits
 * ----------------------------------------------------------------------- */

/**
 * Where an appearance's portrait lives (docs/5 § D260806-1118).
 *
 * A URL RATHER THAN A FETCHER, and both callers want it that way: `lib/bird-portrait`
 * uses it as a Cache Storage key as well as a request, so the address has to be one
 * string produced in one place. It is composed here because `BASE` differs by
 * channel — a relative path on the web so the session cookie is same-origin, an
 * absolute Worker URL under Capacitor where a relative path resolves to the bundle.
 *
 * NOT ENCODED, because it cannot need it: `birdCode` emits lowercase letters,
 * digits, dots and dashes only, and the server refuses anything that is not
 * character-for-character a code it would have written itself.
 */
export const birdPortraitUrl = (code: string): string => `${BASE}/birds/${code}`;

/**
 * Offer this device's capture, so nobody else has to make it.
 *
 * IT DOES NOT GO THROUGH `call()`, and that is the exception rather than a
 * shortcut: `call` sets a JSON content type, parses a JSON response and throws a
 * typed error, and this sends PNG bytes and cares about none of the three. What it
 * does have to keep is the two things `call`'s comment says are easy to forget —
 * `credentials` and the native bearer token — so both are here.
 *
 * FAILURE IS NOT THE CALLER'S PROBLEM. Every refusal is expected traffic: somebody
 * seeded it first, the daily ceiling, an expired session, no network. The portrait
 * is already on screen either way, so this resolves rather than throws and the
 * caller does not branch on it.
 */
export async function seedBirdPortrait(code: string, png: Blob): Promise<void> {
  await fetch(birdPortraitUrl(code), {
    method: 'PUT',
    headers: {
      'content-type': 'image/png',
      ...(bearerToken ? { authorization: `Bearer ${bearerToken}` } : {}),
    },
    body: png,
    credentials: 'include',
  }).catch(() => undefined);
}

export const checkHandle = (candidate: string) =>
  call<{ available: boolean; message?: string }>(
    `/me/handle?q=${encodeURIComponent(candidate)}`,
  );

export const getMySpaces = () => call<{ spaces: PublicSpace[] }>('/me/spaces');

/* --------------------------------------------------------------------------
 * Sign in
 * ----------------------------------------------------------------------- */

export const getProviders = () =>
  call<{ providers: string[]; password: boolean }>('/auth/providers');

export const signInWithPassword = (email: string, password: string) =>
  call<unknown>('/auth/sign-in/email', {
    method: 'POST',
    /* Be explicit: Capacitor's native bridge preserves authored headers, unlike
       browser Fetch's inferred URLSearchParams header. Keeping the encoding and
       header together makes a sign-in request valid in both environments. */
    headers: {
      'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: new URLSearchParams({ email, password }),
  });

export const signUpWithPassword = (email: string, password: string, name: string) =>
  call<unknown>('/auth/sign-up/email', {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: new URLSearchParams({ email, password, name }),
  });

export const signOut = () => call<unknown>('/auth/sign-out', { method: 'POST' });

/**
 * Begin a social sign-in, and return the URL the browser must go to.
 *
 * TWO STEPS, NOT ONE, AND THE FIRST IS A POST. It is tempting to build the
 * authorize URL and navigate straight to it — that was the first version of this
 * function, and it 404'd: Better Auth's `/sign-in/social` accepts POST only, so a
 * `window.location.href = …` navigation (a GET) never reached it and the button
 * was dead. The endpoint answers with `{ url }`, and that is what the browser
 * follows.
 *
 * WHY IT CANNOT BE A PLAIN REDIRECT ANYWAY: the POST is what mints the `state`
 * and the PKCE verifier and stores them against the session. A client that
 * assembled its own authorize URL would be assembling one with no state to
 * validate on the way back, which is the whole attack `state` exists to stop.
 *
 * `errorCallbackURL` is what keeps a failure on our own screens. Without it a
 * refused consent, an unregistered redirect URI or an account-linking conflict
 * lands the person on Better Auth's bare error page with no way back — and the
 * most likely failure early on is mundane: a Google client still in Testing mode
 * refuses anybody who is not a listed test user.
 */
export async function startSocialSignIn(
  provider: string,
  callbackURL: string,
  errorCallbackURL: string,
): Promise<string> {
  const { url } = await call<{ url?: string; redirect?: boolean }>(
    '/auth/sign-in/social',
    {
      method: 'POST',
      body: JSON.stringify({ provider, callbackURL, errorCallbackURL }),
    },
  );
  if (!url) {
    throw new ApiError(500, 'internal', "That sign-in method isn't available.");
  }
  return url;
}

/* --------------------------------------------------------------------------
 * Reading
 * ----------------------------------------------------------------------- */

export interface FeedGroup {
  id: string;
  posts: PostData[];
}

export interface FeedPage {
  groups: FeedGroup[];
  cursor: string | null;
}

export const getFeed = (cursor?: string, signal?: AbortSignal) =>
  call<FeedPage>(
    `/posts/feed${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    { signal },
  );

/** A synthetic page that contains no account or database content. */
export const getRouteCanaryFeed = () =>
  call<FeedPage>('/posts/canary/feed');

/**
 * One open letter and the comments under it.
 *
 * AN ID THAT NAMES A COMMENT ANSWERS WITH THE LETTER IT IS IN, which is the server's
 * rule and not this client's (see `GET /posts/:id`). So a caller may hand it whatever
 * id the card it is holding carries, and the payload is always a whole conversation —
 * which is why the cache key is the id of the letter that comes BACK rather than the
 * one that went in (`keys.letter`).
 */
export const getPost = (id: string) =>
  call<{
    post: PostData;
    comments: PostData[];
    commentsSeenThrough: { at: number; id: string } | null;
  }>(
    `/posts/${encodeURIComponent(id)}`,
  );

export const markCommentsRead = (id: string, seenThroughId: string) =>
  call<{ read: true }>(`/posts/${encodeURIComponent(id)}/comments/read`, {
    method: 'POST',
    body: JSON.stringify({ seenThroughId }),
  });

export const getProfile = (handle: string) =>
  call<{
    profile: PublicProfile;
    relation: ViewerRelation | null;
    /* What the card's bread button would do, `null` on your own card
       (docs/5 § D260808-2014). It rides with the card rather than being asked for
       separately, because bread now goes wherever a follow goes and the loft
       cannot answer for a person you do not write to. */
    gift: CrumbGiftOffer | null;
    posts: PostData[];
  }>(`/profiles/${encodeURIComponent(handle)}`);

export const getPeople = () =>
  call<{ followers: PublicProfile[]; following: PublicProfile[] }>(
    '/profiles/me/people',
  );

/**
 * Everybody an `@` may name (docs/5 § D260809-0021).
 *
 * IT TAKES NO QUERY, WHICH IS THE FEATURE. The whole list arrives once and the
 * narrowing happens on the device — see `lib/mentions` for the scan and the
 * route's own comment for why a `?q=` here would be a people search this product
 * does not have. What it costs a composer is one request, cached; what the
 * alternative costs is one per character typed.
 */
export const getMentionable = () =>
  call<{ people: MentionCandidate[] }>('/profiles/me/mentionable');

export const getSpaces = () =>
  call<{ spaces: PublicSpace[]; joined: string[]; defaultSlug: string }>('/spaces');

/**
 * A page of the room directory, narrowed by `q` when there is one.
 *
 * AN EMPTY `q` IS THE WHOLE DIRECTORY rather than an empty answer, which is what lets
 * one query serve both browsing and searching (docs/5 § D260805-1500). Ordered by how
 * much has been written in each room; `cursor` is the server's and is opaque here.
 */
export const searchSpaces = (q: string, cursor?: string) =>
  call<{ spaces: SpaceListing[]; cursor: string | null }>(
    `/spaces/search?q=${encodeURIComponent(q)}${
      cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
    }`,
  );

/**
 * Ask for a room that is not there (docs/5 § D260805-1500b).
 *
 * IDEMPOTENT ON THE SERVER, so a caller never has to know whether it has already
 * asked: the same name from the same account twice is one wish and a 200. What it can
 * still refuse is a name that already exists and one suggestion too many for today,
 * and both arrive as an `ApiError` with a sentence to render.
 */
export const suggestSpace = (name: string) =>
  call<{ suggested: true }>('/spaces/suggestions', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const getSpace = (slug: string) =>
  call<{ space: PublicSpace; joined: boolean; posts: PostData[] }>(
    `/spaces/${encodeURIComponent(slug)}`,
  );

export const getNotifications = () =>
  call<{ items: NotificationItem[] }>('/notifications');

/**
 * Both dots in the tab bar, in one answer (docs/5 § D260812-1637).
 *
 * `unread` is the You tab — things that happened in the open, cleared by looking
 * at the notifications page. `letters` is the Loft tab — a letter that has landed
 * and is not opened, cleared by opening it. The server keeps them apart so that
 * one arrival cannot light both; see `GET /unread` for why that split is the
 * whole design rather than a tidy-up.
 *
 * TWO COUNTS, AND NOTHING RENDERS EITHER AS A NUMBER. `useUnreadDot` narrows both
 * to booleans at the only call site there is.
 */
export const getUnreadCount = () =>
  call<{ unread: number; letters: number }>('/notifications/unread');

export const markNotificationsRead = () =>
  call<unknown>('/notifications/read', { method: 'POST' });

export const getLoft = () => call<{ entries: LoftEntry[] }>('/letters');

/* --------------------------------------------------------------------------
 * Breadcrumbs (docs/5 § D260806-2033)
 *
 * THE READ IS ALSO A WRITE, and it is the only one in this file. `GET /crumbs`
 * accrues the daily ration on its way past, so the balance a caller gets back
 * may be larger than the one that was there a moment ago. That is a fact about
 * the endpoint rather than a quirk of the client, and `lib/crumbs.ts` on the
 * server carries the argument for why the ration lands on a read.
 * ----------------------------------------------------------------------- */

export const getCrumbs = () => call<CrumbPurse>('/crumbs');

/** Scatter feed. Answers with the balance afterwards, so nothing has to guess. */
export const scatterFeed = () =>
  call<{ crumbs: number }>('/crumbs/feed', { method: 'POST' });

export const getGiftTargets = () =>
  call<{ friends: CrumbGiftTarget[] }>('/crumbs/friends');

export const giftCrumbs = (handle: string) =>
  call<{ crumbs: number }>('/crumbs/gift', {
    method: 'POST',
    body: JSON.stringify({ handle }),
  });

export interface DoveBackgroundResult {
  crumbs: number;
  background: DoveBackgroundId;
  ownedBackgrounds: DoveBackgroundId[];
  transaction: 'purchased' | 'equipped';
}

/** Buy a sill background once, or equip it when it is already owned. */
export const keepDoveBackground = (background: DoveBackgroundId) =>
  call<DoveBackgroundResult>('/crumbs/background', {
    method: 'POST',
    body: JSON.stringify({ background }),
  });

export const getCorrespondence = (handle: string) =>
  call<{ other: PublicAuthor; letters: LetterData[] }>(
    `/letters/${encodeURIComponent(handle)}`,
  );

/* --------------------------------------------------------------------------
 * Writing
 * ----------------------------------------------------------------------- */

/**
 * Write an open letter, or a comment on one.
 *
 * THE TWO SHAPES ARE EXCLUSIVE AND THE TYPE SAYS SO. A letter names a room; a comment
 * names the letter it answers and no room at all, because a comment is in the room its
 * letter is in and the server takes it from the parent — it *refuses* a `spaceId` sent
 * with a `replyToId` rather than ignoring one, so a union here is the difference
 * between a compile error and a 422 at the end of somebody's typing.
 */
export const writePost = (
  input: {
    body: string;
    place?: string;
    /** A GIF (docs/5 § D260805-1220). What goes on the wire is narrower than a
     *  `GifPick`: no `previewUrl`, because the small animated encoding is the
     *  picker's grid and a letter never draws it. */
    gif?: Omit<GifPick, 'previewUrl'>;
    /** Or up to POST_MAX_IMAGES stills, already uploaded (docs/5 § D260808-2252).
     *  Never both — the server refuses a mixture rather than choosing one, and
     *  the composer never offers it. `aspect` is not sent: the server read the
     *  dimensions out of the stored bytes and does not take a client's word for
     *  the shape of a file. */
    images?: { mediaId: string; alt: string }[];
    poll?: { multiple: boolean; options: string[] };
  } & (
    | { spaceId: string; replyToId?: never }
    | { replyToId: string; spaceId?: never }
  ),
) =>
  /* `post` is the server's own projection of what was just written, and it is
     OPTIONAL for the reason `writeLetter`'s letter is: the Worker and the SPA ship
     separately (docs/5 § D260806-1241), so a client can be newer than the API it is
     talking to. The caller falls back to invalidation rather than reading through
     `undefined`. */
  call<{ id: string; post?: PostData }>('/posts', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const votePoll = (id: string, optionIds: string[]) =>
  call<{ voted: boolean }>(`/posts/${encodeURIComponent(id)}/poll`, {
    method: 'PUT', body: JSON.stringify({ optionIds }),
  });

/* --------------------------------------------------------------------------
 * GIFs (docs/5 § D260805-1220)
 * ----------------------------------------------------------------------- */

/**
 * A page of GIFs — a search when there is a query, the popular set when there is
 * not (docs/5 § D260805-1221).
 *
 * `pos` IS THE PROVIDER'S CURSOR AND IT IS OPAQUE HERE TOO. It came from `next` on
 * the previous page and it goes back untouched; nothing in this client knows what
 * is inside it, which is the property that makes the paging survive the provider
 * changing its mind.
 */
export const getGifs = (input: { q: string; pos?: string }) => {
  const params = new URLSearchParams();
  if (input.q) params.set('q', input.q);
  if (input.pos) params.set('pos', input.pos);
  const query = params.toString();
  return call<GifPage>(`/gifs${query ? `?${query}` : ''}`);
};

/** The ones you starred, newest first. Bounded by `GIF_FAVOURITES_MAX`, so it is
 *  one call with no paging. */
export const getGifFavourites = () => call<{ results: GifPick[] }>('/gifs/favourites');

/**
 * Star one, or unstar it.
 *
 * THE WHOLE RECORD GOES UP, NOT JUST THE ID, because the favourites grid has to
 * draw without asking the provider anything — see `gif_favourite` in the schema for
 * why that is worth a copy of six fields.
 */
export const starGif = (gif: GifPick) => {
  const { id, ...rest } = gif;
  return call<void>(`/gifs/favourites/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(rest),
  });
};

export const unstarGif = (id: string) =>
  call<void>(`/gifs/favourites/${encodeURIComponent(id)}`, { method: 'DELETE' });

/*
 * CHANGING YOUR MIND (docs/5 § D260805-0418). Four calls, and a direct letter
 * is addressed through its correspondence rather than by id alone — the server
 * scopes the letter to the thread the handle names, and a client that knew only the
 * id would be inviting it not to.
 *
 * `edited` COMES BACK RATHER THAN BEING ASSUMED. A rewrite of a queued letter is
 * not an edit and takes no mark, so the caller cannot know from the request alone
 * whether the sheet now says `(edited)` — the answer is the server's.
 */
export const editPost = (id: string, body: string) =>
  call<{ edited: boolean }>(`/posts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });

export const deletePost = (id: string) =>
  call<{
    deleted: boolean;
    parentId?: string;
    hasComments?: boolean;
  }>(`/posts/${id}`, { method: 'DELETE' });

export const editLetter = (handle: string, id: string, body: string) =>
  call<{ edited: boolean }>(
    `/letters/${encodeURIComponent(handle)}/${id}`,
    { method: 'PATCH', body: JSON.stringify({ body }) },
  );

export const deleteLetter = (handle: string, id: string) =>
  call<{ deleted: boolean }>(
    `/letters/${encodeURIComponent(handle)}/${id}`,
    { method: 'DELETE' },
  );

export const writeLetter = (handle: string, body: string) =>
  call<{ id: string; leavesOn: string; letter?: LetterData }>(
    `/letters/${encodeURIComponent(handle)}`,
    { method: 'POST', body: JSON.stringify({ body }) },
  );

export const openCorrespondence = (handle: string) =>
  call<unknown>(`/letters/${encodeURIComponent(handle)}/open`, { method: 'POST' });

export const setLike = (id: string, on: boolean) =>
  call<{ liked: boolean }>(`/posts/${id}/like`, { method: on ? 'PUT' : 'DELETE' });

export const setFollow = (handle: string, on: boolean) =>
  call<{ following: boolean }>(
    `/profiles/${encodeURIComponent(handle)}/follow`,
    { method: on ? 'PUT' : 'DELETE' },
  );

export const setMembership = (slug: string, joined: boolean) =>
  call<{ joined: boolean }>(
    `/spaces/${encodeURIComponent(slug)}/membership`,
    { method: joined ? 'PUT' : 'DELETE' },
  );

/* --------------------------------------------------------------------------
 * Photographs
 * ----------------------------------------------------------------------- */

export const requestCaptureToken = () =>
  call<{ token: string; expiresInMs: number }>('/media/capture-token', {
    method: 'POST',
  });

/**
 * Send the captured bytes.
 *
 * NOT THROUGH `call()`, because the body is a Blob rather than JSON and the
 * content type has to be the image's own. It still carries credentials and the
 * bearer header, which is the part that matters.
 */
export async function uploadAvatar(blob: Blob, token: string): Promise<{ id: string; url: string }> {
  return putBytes<{ id: string; url: string }>('/media/avatar', blob, {
    'x-capture-token': token,
  });
}

/**
 * Send one scaled still for an open letter (docs/5 § D260808-2252).
 *
 * NO TOKEN, WHICH IS THE DIFFERENCE BETWEEN THE TWO ROUTES ABOVE AND BELOW: a
 * lead photograph is captured because it is evidence about a person, and a still
 * is chosen because it is a picture of something else. `routes/media.ts` carries
 * the argument.
 *
 * IT HAPPENS WHILE THE LETTER IS BEING WRITTEN, not when Send is pressed, so a
 * photograph that will not upload fails beside the thumbnail rather than on top
 * of four hundred words. What comes back is the dimensions the SERVER read out of
 * the stored bytes — the client's own numbers are not sent, because a ratio the
 * layout reserves space from should be a fact about the file.
 */
export async function uploadPostImage(
  blob: Blob,
  signal?: AbortSignal,
): Promise<{ id: string; url: string; width: number; height: number }> {
  return putBytes('/media/post-image', blob, {}, signal);
}

/**
 * PUT a Blob, and read the error the way `call()` does.
 *
 * NOT THROUGH `call()` ITSELF, because the body is bytes rather than JSON and the
 * content type has to be the image's own — but everything after the request is
 * identical, which is why it is written once here rather than twice above.
 */
async function putBytes<T>(
  path: string,
  blob: Blob,
  extra: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': blob.type || 'image/jpeg',
    ...extra,
  };
  if (bearerToken) headers.authorization = `Bearer ${bearerToken}`;

  const res = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    headers,
    body: blob,
    credentials: 'include',
    signal,
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: { code?: string; message?: string } }).error
        : null;
    throw new ApiError(res.status, err?.code ?? 'internal', err?.message ?? 'That did not work.');
  }
  return body as T;
}

/** An avatar's URL, for an `<img>`. Absolute, because a relative one resolves to
 *  the app itself under Capacitor. */
export const mediaUrl = (id: string): string => `${BASE}/media/${id}`;
